import React, { useState, useEffect } from 'react';
import { fsiReportApi, ReportOptions, CustomReportRequest, CustomReportResponse, TallBuildingError } from '@/services/fsiReport';
import FSIUtilizationSelector from './FSIUtilizationSelector';
import DevelopmentScenarioSelector from './DevelopmentScenarioSelector';
import CustomOptionsForm from './CustomOptionsForm';
import CustomReportDisplay from './CustomReportDisplay';
import TallBuildingErrorDisplay from './TallBuildingErrorDisplay';
import { X, CheckCircle, Loader2, MapPin, Building, Calculator } from 'lucide-react';
import { Button } from './ui/button';

interface CustomFSIReportWizardProps {
  gid: number;
  onClose: () => void;
}

const CustomFSIReportWizard: React.FC<CustomFSIReportWizardProps> = ({ gid, onClose }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<TallBuildingError | null>(null);
  const [options, setOptions] = useState<ReportOptions | null>(null);
  const [report, setReport] = useState<CustomReportResponse | null>(null);
  
  const [formData, setFormData] = useState<CustomReportRequest>({
    fsi_utilization: '',
    development_scenario: '',
    custom_options: {
      floor_count: undefined,
      basement_count: 0,
      has_tdr: false,
      tdr_area_sqm: 0,
    },
  });

  useEffect(() => {
    fetchReportOptions();
  }, [gid]);

  const fetchReportOptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fsiReportApi.getReportOptions(gid);
      // Calculate area_sqft if not provided by API (1 sqm = 10.764 sqft)
      if (data.plot_details.area_sqm && !data.plot_details.area_sqft) {
        data.plot_details.area_sqft = data.plot_details.area_sqm * 10.764;
      }
      setOptions(data);
      // Set default FSI utilization
      if (data.fsi_options.length > 0) {
        setFormData(prev => ({
          ...prev,
          fsi_utilization: data.fsi_options[0].id,
        }));
      }
    } catch (err: any) {
      console.error('Error fetching report options:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!formData.fsi_utilization || !formData.development_scenario) {
      alert('Please select FSI utilization and development scenario');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const reportData = await fsiReportApi.generateCustomReport(gid, formData);
      setReport(reportData);
      setStep(3);
    } catch (err: any) {
      const errorData = err.response?.data;
      if (errorData?.error_type === 'tall_building_not_eligible') {
        setError(errorData);
        setStep(3);
      } else {
        console.error('Error generating report:', err);
        alert('Failed to generate report. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAlternativeSelect = (floors: number) => {
    setFormData(prev => ({
      ...prev,
      custom_options: {
        ...prev.custom_options,
        floor_count: floors,
      },
    }));
    setError(null);
    setStep(2);
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      <div className={`flex items-center ${step >= 1 ? 'text-green-600' : 'text-gray-400'}`}>
        <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
          step >= 1 ? 'border-green-600 bg-green-50' : 'border-gray-300 bg-white'
        }`}>
          {step > 1 ? <CheckCircle className="w-5 h-5" /> : <span className="font-bold">1</span>}
        </div>
        <span className="ml-2 font-medium hidden sm:inline">FSI Strategy</span>
      </div>
      <div className={`w-16 sm:w-24 h-0.5 mx-2 ${step >= 2 ? 'bg-green-600' : 'bg-gray-300'}`} />
      <div className={`flex items-center ${step >= 2 ? 'text-green-600' : 'text-gray-400'}`}>
        <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
          step >= 2 ? 'border-green-600 bg-green-50' : 'border-gray-300 bg-white'
        }`}>
          {step > 2 ? <CheckCircle className="w-5 h-5" /> : <span className="font-bold">2</span>}
        </div>
        <span className="ml-2 font-medium hidden sm:inline">Development Type</span>
      </div>
      <div className={`w-16 sm:w-24 h-0.5 mx-2 ${step >= 3 ? 'bg-green-600' : 'bg-gray-300'}`} />
      <div className={`flex items-center ${step >= 3 ? 'text-green-600' : 'text-gray-400'}`}>
        <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
          step >= 3 ? 'border-green-600 bg-green-50' : 'border-gray-300 bg-white'
        }`}>
          <span className="font-bold">3</span>
        </div>
        <span className="ml-2 font-medium hidden sm:inline">View Report</span>
      </div>
    </div>
  );

  if (loading && !options) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8">
          <div className="flex flex-col items-center justify-center">
            <Loader2 className="w-12 h-12 text-green-600 animate-spin mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">Loading Report Options</h3>
            <p className="text-gray-600 text-center">Fetching available development options for this plot...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto my-8">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Custom FSI Report Generator</h2>
              <p className="text-sm text-gray-600">Create detailed development analysis for GID: {gid}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 py-4 bg-gray-50">
          {renderStepIndicator()}
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {options && (
            <>
              {/* Plot Summary */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 mb-6 border border-green-200">
                <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-green-600" />
                  Plot Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <label className="text-xs text-gray-500 font-medium">Plot Area</label>
                    <p className="text-lg font-bold text-gray-800">
                      {options.plot_details.area_sqm ? options.plot_details.area_sqm.toFixed(2) : 'N/A'} m²
                    </p>
                    <p className="text-sm text-gray-600">
                      ({options.plot_details.area_sqft ? options.plot_details.area_sqft.toFixed(0) : 'N/A'} sqft)
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <label className="text-xs text-gray-500 font-medium">Zone</label>
                    <p className="text-lg font-bold text-gray-800">
                      <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full">
                        {options.plot_details.zone || 'N/A'}
                      </span>
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <label className="text-xs text-gray-500 font-medium">Tall Building</label>
                    <p className={`text-lg font-bold ${
                      options.plot_details.tall_building_eligible ? 'text-green-600' : 'text-orange-600'
                    }`}>
                      {options.plot_details.tall_building_eligible ? '✓ Eligible' : '✗ Not Eligible'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 1: FSI Utilization */}
              {step >= 1 && (
                <FSIUtilizationSelector
                  options={options.fsi_options}
                  zoneFsiConfig={options.zone_fsi_config}
                  selected={formData.fsi_utilization}
                  onChange={(value: string) =>
                    setFormData((prev) => ({ ...prev, fsi_utilization: value }))
                  }
                />
              )}

              {/* Step 2: Development Scenario & Custom Options */}
              {step >= 2 && formData.fsi_utilization && (
                <>
                  <DevelopmentScenarioSelector
                    scenarios={options.development_scenarios}
                    zone={options.plot_details.zone}
                    plotAreaSqft={options.plot_details.area_sqft || 0}
                    selected={formData.development_scenario}
                    onChange={(value: string) =>
                      setFormData((prev) => ({ ...prev, development_scenario: value }))
                    }
                  />

                  {formData.development_scenario && (
                    <CustomOptionsForm
                      scenario={options.development_scenarios.find(
                        (s) => s.id === formData.development_scenario
                      )}
                      fsiUtilization={formData.fsi_utilization}
                      zoneFsiConfig={options.zone_fsi_config}
                      customOptions={formData.custom_options!}
                      onChange={(value: any) =>
                        setFormData((prev) => ({
                          ...prev,
                          custom_options: { ...prev.custom_options, ...value },
                        }))
                      }
                    />
                  )}
                </>
              )}

              {/* Step 3: Report Display or Error */}
              {step === 3 && (
                <>
                  {error?.error_type === 'tall_building_not_eligible' ? (
                    <TallBuildingErrorDisplay
                      error={error}
                      onSelectAlternative={handleAlternativeSelect}
                      onClose={() => {
                        setError(null);
                        setStep(2);
                      }}
                    />
                  ) : report ? (
                    <CustomReportDisplay report={report} />
                  ) : null}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            {step > 1 && step < 3 && (
              <Button
                onClick={() => setStep(step - 1)}
                variant="outline"
                disabled={loading}
              >
                Previous
              </Button>
            )}
          </div>
          
          <div className="flex gap-3">
            {step === 1 && formData.fsi_utilization && (
              <Button
                onClick={() => setStep(2)}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Next: Select Development Type
              </Button>
            )}
            
            {step === 2 && formData.development_scenario && (
              <Button
                onClick={handleGenerateReport}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Report...
                  </>
                ) : (
                  <>
                    <Building className="w-4 h-4 mr-2" />
                    Generate Custom Report
                  </>
                )}
              </Button>
            )}
            
            {step === 3 && report && (
              <Button
                onClick={() => {
                  setStep(1);
                  setReport(null);
                  setError(null);
                }}
                variant="outline"
              >
                Generate New Report
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomFSIReportWizard;
